import * as React from 'react';
import * as R from 'ramda';
import { BaseRouteProps, BaseFormState } from '../../interfaces';
import { Survey, SurveyVersion, UserTypeahead, SurveyInstance } from '../../models';
import { SurveyDataService, TypeaheadService, SurveyInstanceDataService, ValidationService } from '../../services';
import { NavLink } from 'react-router-dom';
import { Form, Select, FormValues, FormErrors, FormApi } from 'react-form';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { PVAPage } from '../../components';
import { UserStore, FilterStore } from '../../stores';
import { Roles } from '../../stores/Constants';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';
import * as moment from 'moment';
import Datepicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './SurveyInstanceCreate.css';
import { FRONTEND_ROOT, SURVEY_PRICE_ID } from '../../config/host-config';

interface SurveyInstanceCreateState extends BaseFormState {
    surveys: Survey[];
    selectedSurvey: number;
    userTypeahead: {
        allowNew: boolean;
        isLoading: boolean;
        multiple: boolean;
        options: any[];
    };
    confirmedUser?: UserTypeahead;
    selectedDate: moment.Moment;
    datepickerTouched: boolean;
    paidSurveyLink: string;
}

interface SurveyInstanceCreateProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class SurveyInstanceCreate extends React.Component<SurveyInstanceCreateProps, SurveyInstanceCreateState> {
    private typeaheadService = new TypeaheadService(this.props.notificationService);
    private surveyInstanceDataService = new SurveyInstanceDataService(this.props.notificationService);
    private surveyDataService = new SurveyDataService(this.props.notificationService);
    private vs = new ValidationService();
    private formApi: FormApi;

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get organization() {
        return this.props.UserStore!.userInfo.organization;
    }

    @computed get currOrganization() {
        return this.props.FilterStore!.organization;
    }

    constructor(props: SurveyInstanceCreateProps) {
        super(props);

        this.state = {
            // users: new Array<User>(),
            surveys: new Array<Survey>(),
            selectedSurvey: 0,
            userTypeahead: {
                allowNew: false,
                isLoading: false,
                multiple: false,
                options: [],
            },
            canSubmit: false,
            formErrors: {},
            selectedDate: moment().add(7, 'days'),
            datepickerTouched: true,
            paidSurveyLink: ``
        };

        this.save = this.save.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.typeaheadOnChange = this.typeaheadOnChange.bind(this);
        this.validateErrors = this.validateErrors.bind(this);
        this.validateSuccess = this.validateSuccess.bind(this);
    }

    public componentDidMount() {
        this.surveyDataService.getAll().then((surveys: Survey[]) => {
            this.setState({ surveys: surveys });
            if (surveys.length > 0) {
                let defaultSurveyId = surveys[0].id;
                if (this.props.FilterStore && this.props.FilterStore.survey) {
                    defaultSurveyId = this.props.FilterStore.survey.id;
                }
                this.setState(
                    {
                        selectedSurvey: defaultSurveyId
                    }, 
                    () => { 
                        this.formApi.setTouched('surveyVersionID', true);
                        this.forceUpdate();
                    }
                );
            }
        });
    }

    private handleSearch(filter: string) {
        const { userTypeahead } = this.state;

        if (this.currOrganization) {
            this.setState({ userTypeahead: Object.assign({}, userTypeahead, { isLoading: true }) });
            this.typeaheadService.getUserTypeaheadsByOrganization(this.currOrganization.id, filter)
                .then((response: UserTypeahead[]) => {
                    this.setState({
                        userTypeahead: Object.assign(
                            {},
                            userTypeahead,
                            {
                                isLoading: false,
                                options: response
                            }
                        )
                    });
                });
        }
    }

    private typeaheadOnChange(selected: Array<Object | String>) {
        if (selected.length > 0) {
            this.setState(
                { confirmedUser: selected[0] as UserTypeahead },
                () => { this.formApi.setTouched('userID', true); }
            );
        } else {
            this.setState(
                {confirmedUser: undefined},
                () => { this.formApi.setTouched('userID', true); }
            );
        }
    }

    private save(values: any) {
        if (this.state.confirmedUser) {
            values.userID = this.state.confirmedUser.id;
        }

        if (this.state.selectedDate) {
            values.expirationDate = this.state.selectedDate.toDate();
        }

        this.surveyInstanceDataService.create(values).then((newInvite: SurveyInstance) => {
            this.props.history.push(`/surveyinvite/${newInvite.uniqueIdentifier}`);
        });
    }

    private sortSurveyVersions(a: SurveyVersion, b: SurveyVersion) {
        if (a.published !== undefined && b.published === undefined) {
            return -1;
        } else if (a.published === undefined && b.published !== undefined) {
            return 1;
        }
        if (a.active && !b.active) {
            return -1;
        } else if (!a.active && b.active) {
            return 1;
        }
        if (a.version > b.version) {
            return -1;
        } else if (a.version < b.version) {
            return 1;
        }
        return 0;
    }

    private surveyChange(event: any, formValues: any) {
        var surveyId = Number(event.currentTarget.value);
        var selectedVersionId: number | undefined = undefined;
        var selectedSurvey = this.state.surveys.find((survey: Survey) => {
            return survey.id === surveyId;
        });

        if (selectedSurvey && selectedSurvey.surveyVersions.length > 0) {
            selectedSurvey.surveyVersions.sort(this.sortSurveyVersions);
            selectedVersionId = selectedSurvey.surveyVersions[0].id;
        }

        formValues.surveyVersionID = selectedVersionId;

        this.setState(
            {
                selectedSurvey: surveyId
            }, 
            this.forceUpdate);
        // Not sure why this isn't working. It's delayed.   
        // var survey = this.state.surveys.find((s: Survey) => {
        //     return s.id === this.state.selectedSurvey;
        // });
        // if (survey && survey.surveyVersions.length > 0) {
        //     survey.surveyVersions.sort(this.sortSurveyVersions);
        //     formValues.surveyVersionID = survey.surveyVersions[0].id;
        //     this.forceUpdate();
        // }
    }

    // Validation methods
    validateErrors(values: FormValues) {
        const { surveyVersionID } = values;
        let formErrors: any = {};

        if (surveyVersionID) {
            if (!this.vs.required(surveyVersionID)) {
                formErrors.surveyVersionID = 'Survey version is required.';
            }
        }

        if (this.state.datepickerTouched && this.state.selectedDate == null) {
            formErrors.expirationDate = 'Expiration date is required';
        }

        return formErrors;
    }

    validateSuccess(values: FormValues, errors: FormErrors) {
        const { confirmedUser } = this.state;

        if (confirmedUser) {
            values.userID = confirmedUser.id;
        } else {
            values.userID = undefined;
        }

        let expirationDateExists = true;
        if (!this.state.selectedDate) {
            expirationDateExists = false;
        }

        // make sure we have no errors and all the required values
        if (R.isEmpty(errors) && this.vs.validateRequiredFieldsExist(['surveyVersionID', 'userID'], values) && expirationDateExists) {
            this.setState({ canSubmit: true, formErrors: errors });
        } else {
            this.setState({ canSubmit: false, formErrors: errors });
        }

        return errors;
    }
    // end of validation stuff

    handleDatepickerOnChange = (newDate: moment.Moment) => {
        this.setState({ selectedDate: newDate }, () => { this.formApi.setTouched('expirationDate', true); });
    }

    markDatePickerTouched = () => {
        this.setState({ datepickerTouched: true });
    }

    datePickerBlur = () => {
        this.formApi.setTouched('expirationDate', true);
    }

    setFormApi = (api: FormApi) => {
        this.formApi = api;
    }

    public render() {
        const { userTypeahead, formErrors, canSubmit } = this.state;

        const surveyVersionOptions: any[] = [];

        if (this.state.surveys && this.state.surveys.length > 0) {
            var selectedSurvey = this.state.surveys.find((x: Survey) => {
                return x.id === this.state.selectedSurvey;
            });
            if (selectedSurvey && selectedSurvey.surveyVersions) {
                selectedSurvey.surveyVersions.sort(this.sortSurveyVersions.bind(this)).map((surveyVersion: SurveyVersion, index: number) => {                    
                    surveyVersionOptions.push({
                        label: `#${surveyVersion.version} ${surveyVersion.nickname ? surveyVersion.nickname : ''} ${surveyVersion.active ? ' (active)' : ''} ${surveyVersion.published ? '' : ' *'}`,
                        value: surveyVersion.id
                    });
                });
            }
        }

        return (
            <PVAPage>
                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <NavLink to={`/surveyinvite`}>
                            <span className="glyphicon glyphicon-triangle-left" />
                        </NavLink>
                        <PVAPage.Header.Title.Text>
                            Create Survey Invite
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                </PVAPage.Header>

                <PVAPage.Body>
                    <Form
                        onSubmit={submittedValues => this.save(submittedValues)}
                        getApi={this.setFormApi}
                        validateError={this.validateErrors}
                        validateSuccess={this.validateSuccess}
                    >
                        {formApi => (
                            <form onSubmit={formApi.submitForm} id="entityForm">
                                <div className="form-group">
                                    <label>User *</label>
                                    <AsyncTypeahead
                                        {...userTypeahead}
                                        minLength={2}
                                        onSearch={this.handleSearch}
                                        onChange={this.typeaheadOnChange}
                                        placeholder="Search for a user"
                                        labelKey={(option) => `${option.lastName}, ${option.firstName}`}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="expirationDate" className="control-label">Expiration Date *</label>
                    
                                    <Datepicker
                                        selected={this.state.selectedDate}
                                        showMonthDropdown
                                        showYearDropdown
                                        className="form-control"
                                        onChange={this.handleDatepickerOnChange}
                                        dropdownMode="select"
                                        minDate={moment().add(1, 'days')}
                                        onFocus={this.markDatePickerTouched}
                                        onBlur={this.datePickerBlur}
                                    />
                                    {this.state.formErrors.expirationDate && <span className="help-block error-text">{this.state.formErrors.expirationDate}</span>}
                                </div>
                                <div className="row">
                                    <div className="col-xs-8">
                                        {this.state.surveys && this.state.surveys.length > 1 && <div className="form-group">
                                            <label htmlFor="surveyId">Survey *</label>
                                            <select className="form-control" id="surveyId" value={this.state.selectedSurvey} onChange={(e: any) => this.surveyChange(e, formApi.values)}>
                                                {
                                                    this.state.surveys.map((survey: Survey) => {
                                                        return <option key={survey.id} value={survey.id}>{survey.name}</option>;
                                                    })
                                                }
                                            </select>
                                        </div>}
                                        {this.state.surveys.length === 1 && this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate]) && <div>
                                            {this.state.surveys[0].name}
                                        </div> }
                                    </div>
                                    {this.setSurveyVersionId(formApi.values) && this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate]) && 
                                    <div className="col-xs-4">
                                        <div className="form-group">
                                            <label htmlFor="surveyVersionID">Version *</label>
                                            {this.setSurveyVersionId(formApi.values) && <Select className="form-control" field="surveyVersionID" id="surveyVersionID" options={surveyVersionOptions} />}
                                            {formErrors.surveyVersionID && <span className="help-block error-text">{formErrors.surveyVersionID}</span>}
                                        </div>
                                    </div>}
                                </div>
                                <div className="row actions-row">
                                    <button type="submit" className="btn btn-primary col-xs-1" disabled={!canSubmit}>Send</button>
                                    {/*<input type="text" className="col-xs-5 pull-right paid-link" disabled={true} value={this.state.paidSurveyLink} />*/}
                                    {/*<button type="button" className="btn btn-primary col-xs-2 col-xs-offset-3 pull-right paid-link-button" disabled={!canSubmit} onClick={this.createPaidLink}>Create paid link</button>*/}
                                </div>
                            </form>
                        )}
                    </Form>
                </PVAPage.Body>
                <PVAPage.Footer />
            </PVAPage>);
    }

    private setSurveyVersionId(formValues: any) {
        if (formValues.surveyVersionID === undefined) {
            var surveyId = this.state.selectedSurvey;
            var selectedVersionId: number | undefined = undefined;
            var selectedSurvey = this.state.surveys.find((survey: Survey) => {
                return survey.id === surveyId;
            });
    
            if (selectedSurvey && selectedSurvey.surveyVersions.length > 0) {
                selectedSurvey.surveyVersions.sort(this.sortSurveyVersions);
                selectedVersionId = selectedSurvey.surveyVersions[0].id;
            }
    
            formValues.surveyVersionID = selectedVersionId;
        }
        
        return true;
    }

    private createPaidLink = async () => {
        this.setState({canSubmit: false});

        let values: SurveyInstance = new SurveyInstance();

        if (!!this.state.confirmedUser) {
            values.userID = this.state.confirmedUser.id;
        }

        if (!!this.state.selectedDate) {
            values.expirationDate = this.state.selectedDate.toDate();
        }

        values.surveyVersionID = this.state.selectedSurvey;
        values.priceId = SURVEY_PRICE_ID;

        const newInvite = await this.surveyInstanceDataService.create(values);
        this.setState({paidSurveyLink: `${FRONTEND_ROOT}/assessment/${newInvite.uniqueIdentifier}`});
    }
}


// WEBPACK FOOTER //
// ./src/components/surveyinstance/SurveyInstanceCreate.tsx